// frontend/src/components/login/Login.jsx

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { useAuth } from '../../contexts/AuthContext';
import { sessionService } from '../../services/SessionApiService';
import { translateRequestError } from '../../queries/api';
import { ICON, Eye, EyeSlash } from '../../icons';
import { useTranslation } from 'react-i18next';

export default function LoginPage() {
  const { login, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [csrfReady, setCSRFReady] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // One-shot "session expired / terminated" notice the auth layer set before
  // redirecting here. Derived at mount (no setState-in-effect); the flags are
  // consumed in the effect below so a later normal login doesn't re-show it.
  const [sessionExpiredMessage] = useState(() => {
    if (sessionStorage.getItem('sessionExpired') !== 'true') return '';
    return sessionStorage.getItem('sessionExpiredReason') === 'expired'
      ? t('login.messages.sessionExpired')
      : t('login.messages.sessionTerminated');
  });
  const [theme] = useState(() => localStorage.getItem('theme') || 'light');

  const logoSrc = '/images/manexys-logo.png';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Consume the one-shot session flags (the message is derived above at mount).
  // Unconditional removeItem is a no-op when absent.
  useEffect(() => {
    sessionStorage.removeItem('sessionExpired');
    sessionStorage.removeItem('sessionExpiredReason');
  }, []);

  useEffect(() => {
    // initializeAuth declared later in same scope; closure-captured at first render. Hoisting is the proper fix; flagged as a separate refactor.
    // eslint-disable-next-line react-hooks/immutability
    initializeAuth();
    // Mount-only auth init — initializeAuth is defined below in the
    // component body and is re-created every render; including it in
    // deps would re-fire CSRF init on every state change. Behavior is
    // intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeAuth = async () => {
    try {
      await sessionService.initCSRF();
      setCSRFReady(true);
    } catch (error) {
      console.error('Error initializing authentication:', error);
      setError(translateRequestError(error, t, 'login.errors.failed'));
      setCSRFReady(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError(t('login.errors.required')); return; }
    if (!csrfReady) { setError(t('login.errors.initializing')); return; }
    setIsLoading(true);
    setError('');
    try {
      await login({ email, password, rememberMe });
    } catch (error) {
      console.error('Login error:', error.message);
      // The three refusals with a meaning of their own; anything else is a
      // failed request, and its cause decides what to do next.
      if (error.status === 401) setError(t('login.errors.invalidCredentials'));
      else if (error.status === 403) setError(t('login.errors.accessForbidden'));
      else if (error.status === 429) setError(t('login.errors.rateLimited'));
      else setError(translateRequestError(error, t, 'login.errors.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">

      {/* Left: brand panel */}
      <div className="login-brand">
        <div className="login-brand-content">
          <img src={logoSrc} alt="Manexys" className="login-logo" />
          <div className="login-brand-accent" />
          <p className="login-tagline">Forecast with Precision.<br />Execute with Confidence.</p>
          <ul className="login-features">
            <li>Intelligent multi-model forecasting</li>
            <li>Smart inventory optimization</li>
            <li>Automated purchase orders</li>
          </ul>
        </div>
        <div className="login-brand-footer">
          <span>manexys.com</span>
        </div>
      </div>

      {/* Right: form panel */}
      <div className="login-panel">
        <div className="login-form-wrap">

          {!csrfReady ? (
            <div className="login-initializing">
              <div className="login-spinner" />
              <div className="login-initializing-text">{t('login.initializingConnection')}</div>
            </div>
          ) : (
            <>
              <h1 className="login-title">{t('login.welcomeBack')}</h1>

              {sessionExpiredMessage && (
                <div className="login-info login-info-warning">{sessionExpiredMessage}</div>
              )}
              {error && (
                <div className="login-info login-info-error">{error}</div>
              )}

              <form className="login-form" onSubmit={handleSubmit}>
                <div className="login-input-group">
                  <input
                    type="email"
                    placeholder={t('login.email')}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="login-input"
                    disabled={isLoading || authLoading}
                    autoComplete="email"
                    autoFocus
                  />
                </div>
                <div className="login-input-group">
                  <div className="login-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder={t('login.password')}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="login-input"
                      disabled={isLoading || authLoading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading || authLoading}
                      className="login-input-icon-btn"
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    >
                      {showPassword ? <EyeSlash size={ICON.xl} /> : <Eye size={ICON.xl} />}
                    </button>
                  </div>
                </div>
                <div className="login-remember-row">
                  <div className="login-remember-me">
                    <input
                      type="checkbox"
                      id="remember"
                      checked={rememberMe}
                      onChange={e => setRememberMe(e.target.checked)}
                      className="login-remember-checkbox"
                      disabled={isLoading || authLoading}
                    />
                    <label htmlFor="remember" className="login-remember-label">
                      {t('login.keepSignedIn')}
                    </label>
                  </div>
                  <button
                    type="button"
                    className="login-forgot-link"
                    onClick={() => navigate('/forgot-password')}
                    disabled={isLoading || authLoading}
                  >
                    {t('login.forgotPassword')}
                  </button>
                </div>
                <button
                  type="submit"
                  className="login-button"
                  aria-busy={isLoading || authLoading}
                  disabled={!email || !password || isLoading || authLoading || !csrfReady}
                >
                  {t('login.signIn')}
                </button>
              </form>
            </>
          )}

        </div>
      </div>

    </div>
  );
}
